import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useMemo, useState } from "react";
import { Alert, Table } from "react-bootstrap";
import { ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { getPerformanceReport, listRuleOptions } from "../services/apiClient.js";
import { addInputDays, formatDate } from "../utils/date.js";

// Performance Report — 1.0's per-person blocks as ONE table per period, plus
// the 2.0 badge-completions breakdown (Robert 2026-07-17: show the office
// both; they choose what stays). Non-admins get only their own row (1.0 rule,
// server-enforced).
//
// Dressed as a DOCUMENT (report-document.css) and PORTRAIT: seven columns on the
// by-person table, two on the declined one. The completions pivot grows a column
// per person, which is the only thing here that could outgrow an upright sheet —
// GPS's office is small enough that it does not, and turning the paper for one
// table would waste it on the other two.

const PERIODS = [
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "ytd", label: "Year to Date (Apr)" },
];

const METRICS = [
  ["quotes_typed", "Quotes Typed"],
  ["quotes_checked", "Quotes Checked"],
  ["positive_feedback", "Positive Feedback"],
  ["adverse_feedback", "Adverse Feedback"],
  ["marketing_calls", "Marketing Calls"],
  ["completions", "Badge Completions"],
];

function PerformanceReportPage() {
  const [period, setPeriod] = useState("this_month");
  const [data, setData] = useState(null);
  const [ruleLabels, setRuleLabels] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    listRuleOptions()
      .then((opts) => setRuleLabels(Object.fromEntries(opts.map((o) => [o.key, o.label]))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setError("");
    getPerformanceReport(period)
      .then(setData)
      .catch((err) => setError(err.response?.data?.error || "Unable to load."));
  }, [period]);

  // Completions pivot: rule rows × person columns.
  const pivot = useMemo(() => {
    if (!data) return null;
    const peopleSet = new Set(data.completions_by_rule.map((r) => r.full_name));
    const people = [...peopleSet].sort();
    const rules = new Map();
    for (const r of data.completions_by_rule) {
      const row = rules.get(r.rule_key) || {};
      row[r.full_name] = r.n;
      rules.set(r.rule_key, row);
    }
    return { people, rules: [...rules.entries()] };
  }, [data]);

  const periodLabel = PERIODS.find((p) => p.value === period)?.label || "";

  return (
    <section className="page-panel">
      {/* App chrome: the document carries its own masthead (report-document.css). */}
      <div className="page-heading d-flex flex-wrap justify-content-between align-items-end column-gap-4 no-print">
        <h2>Performance Report <RulesHelp topic="performance" /></h2>
        <ThemedSelect size="sm" style={{ width: 190 }} value={period} onChange={(e) => setPeriod(e.target.value)}>
          {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </ThemedSelect>
      </div>

      {error ? <Alert variant="danger" className="py-2">{error}</Alert> : null}

      {data ? (
        <div className="report-doc border rounded">
          <ReportMasthead
            report="Performance Report"
            title={periodLabel}
            // `end` is EXCLUSIVE on the wire; a document states the last day it
            // actually covers, so it steps back one (addInputDays, not raw Date
            // maths, so the machine timezone cannot move it).
            subtitle={`Grayburn Property Services · ${formatDate(data.start)} to ${formatDate(addInputDays(data.end, -1))}`}
          />
          <div className="report-body">
          <p className="text-muted small mb-2">
            QCC: {data.qcc.inspections} inspection{data.qcc.inspections === 1 ? "" : "s"} on {data.qcc.accepted_jobs} accepted job{data.qcc.accepted_jobs === 1 ? "" : "s"},{" "}
            {data.qcc.passed} passed{data.qcc.inspections > 0 ? ` (${Math.round((100 * data.qcc.passed) / data.qcc.inspections)}%)` : ""}
          </p>

          {/* Bare .report-block-title, not <ReportBlock>: a block is
              break-inside: avoid, which would stop these tables paginating. */}
          <h3 className="report-block-title">By person</h3>
          <Table striped bordered hover responsive size="sm" className="align-middle report-table" style={{ maxWidth: 900 }}>
            <thead className="table-surface">
              <tr><th>Person</th>{METRICS.map(([k, l]) => <th key={k} className="text-end">{l}</th>)}</tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.full_name}>
                  <td>{r.full_name}</td>
                  {METRICS.map(([k]) => <td key={k} className="text-end">{r[k] ?? ""}</td>)}
                </tr>
              ))}
              {data.rows.length === 0 ? <tr><td colSpan={7} className="text-center text-muted">Nothing recorded this period.</td></tr> : null}
            </tbody>
          </Table>

          <h3 className="report-block-title mt-3">Badge completions by rule</h3>
          <p className="text-muted small mb-2">
            What each person CLEARED off the needs-attention lists — the 2.0 view of performance.
          </p>
          {pivot && pivot.rules.length > 0 ? (
            <Table striped bordered hover responsive size="sm" className="align-middle report-table" style={{ maxWidth: 900 }}>
              <thead className="table-surface">
                <tr><th>Rule</th>{pivot.people.map((p) => <th key={p} className="text-end">{p}</th>)}</tr>
              </thead>
              <tbody>
                {pivot.rules.map(([key, row]) => (
                  <tr key={key}>
                    <td>{ruleLabels[key] || key}</td>
                    {pivot.people.map((p) => <td key={p} className="text-end">{row[p] ?? ""}</td>)}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted small">No badge completions recorded this period.</p>
          )}

          <h3 className="report-block-title mt-3">Declined jobs by reason</h3>
          <Table striped bordered hover responsive size="sm" className="align-middle report-table" style={{ maxWidth: 420 }}>
            <thead className="table-surface"><tr><th>Reason</th><th className="text-end">Jobs</th></tr></thead>
            <tbody>
              {data.declined.map((d) => (
                <tr key={d.reason}><td>{d.reason}</td><td className="text-end">{d.n}</td></tr>
              ))}
              {data.declined.length === 0 ? <tr><td colSpan={2} className="text-center text-muted">None this period.</td></tr> : null}
            </tbody>
          </Table>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default PerformanceReportPage;
